// src/services/taskService.js
import { db, storage } from '../config/firebase';
import { functions } from '../config/firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  addDoc, 
  updateDoc, 
  deleteDoc,
  setDoc,
  writeBatch,
  increment,
  query,
  orderBy,
  where,
  serverTimestamp,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';

const flattenQuestionProgress = (questions) => {
  const flattened = {};
  
  Object.entries(questions || {}).forEach(([key, value]) => {
    // Check if this is a direct question entry (has 'box' property as number)
    if (value && typeof value.box === 'number') {
      flattened[key] = value;
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      // This is a nested group - iterate its children
      Object.entries(value).forEach(([subKey, subValue]) => {
        if (subValue && typeof subValue.box === 'number') {
          flattened[subKey] = subValue;
        }
      });
    }
  });
  
  return flattened;
};

export const taskService = {
  // Create a new task
  async createTask(bookId, taskData, creatorId) {
    try {
      const tasksRef = collection(db, 'pb-books', bookId, 'tasks');
      
      const task = {
        type: taskData.type || 'leitner_quiz',
        name: taskData.name,
        description: taskData.description || '',
        questionIds: taskData.questionIds || [],
        sections: taskData.sections || [],
        guidedReconstruction: taskData.guidedReconstruction || null,
        visibility: taskData.visibility || 'private', // 'private', 'organisation', 'public'
        assignedUsers: taskData.assignedUsers || [], // Array of user emails
        organisationId: taskData.organisationId || null,
        isTimeBound: taskData.isTimeBound || false,
        startDate: taskData.startDate || null,
        endDate: taskData.endDate || null,
        passingScore: taskData.passingScore || 70,
        timeLimitPerQuestion: taskData.timeLimitPerQuestion || 0,
        shuffleQuestions: taskData.shuffleQuestions !== false,
        showAnswersAfterEachResponse: taskData.showAnswersAfterEachResponse === true,
        showAnswersAfter: taskData.showAnswersAfter !== false,
        maxAttempts: taskData.maxAttempts || 0, // 0 = unlimited
        // coLearner settings
        coLearnerEnabled: taskData.coLearnerEnabled !== false,
        coLearnerCrossTask: taskData.coLearnerCrossTask !== false,
        coLearnerPrompt: taskData.coLearnerPrompt || '',
        // OG Meta
        ogTitle: taskData.ogTitle || taskData.name,
        ogDescription: taskData.ogDescription || taskData.description || '',
        ogImage: taskData.ogImage || null, // Will use universal if null
        // Exported task PDF
        taskPdfUrl: taskData.taskPdfUrl || '',
        taskPdfFilename: taskData.taskPdfFilename || '',
        taskPdfUploadedAt: taskData.taskPdfUploadedAt || null,
        // Creator info
        creatorId,
        creatorEmail: taskData.creatorEmail || '',
        // Timestamps
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(tasksRef, task);
      return { id: docRef.id, ...task };
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  },

  // Update task
  async updateTask(bookId, taskId, updates) {
    try {
      const taskRef = doc(db, 'pb-books', bookId, 'tasks', taskId);
      await updateDoc(taskRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return { id: taskId, ...updates };
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  },

  // Delete task
  async deleteTask(bookId, taskId) {
    try {
      const taskRef = doc(db, 'pb-books', bookId, 'tasks', taskId);
      await deleteDoc(taskRef);
      return { success: true };
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  },

  // Get all tasks for a book (owner view)
  async getBookTasks(bookId) {
    try {
      const tasksRef = collection(db, 'pb-books', bookId, 'tasks');
      const q = query(tasksRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching tasks:', error);
      throw error;
    }
  },

  // Get tasks available to a user
  async getAvailableTasks(bookId, userEmail, userId) {
    try {
      const tasksRef = collection(db, 'pb-books', bookId, 'tasks');
      const snapshot = await getDocs(tasksRef);
      
      const now = new Date();
      
      const tasks = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(task => {
          // Check visibility
          if (task.visibility === 'public') return true;
          if (task.visibility === 'private' && task.assignedUsers?.includes(userEmail)) return true;
          if (task.creatorId === userId) return true;
          return false;
        })
        .filter(task => {
          // Check time bounds
          if (!task.isTimeBound) return true;
          
          const startDate = task.startDate?.toDate?.() || new Date(task.startDate);
          const endDate = task.endDate?.toDate?.() || new Date(task.endDate);
          
          return now >= startDate && now <= endDate;
        });
      
      return tasks;
    } catch (error) {
      console.error('Error fetching available tasks:', error);
      throw error;
    }
  },

  // Load coLearner conversation thread for a user + task
  async getCoLearnerThread(bookId, taskId, userId) {
    try {
      const threadRef = doc(db, 'pb-books', bookId, 'tasks', taskId, 'coLearnerThreads', userId);
      const threadDoc = await getDoc(threadRef);
      if (!threadDoc.exists()) return null;
      return { id: threadDoc.id, ...threadDoc.data() };
    } catch (error) {
      console.error('Error loading coLearner thread:', error);
      return null;
    }
  },

  // Save coLearner conversation thread for a user + task
  async saveCoLearnerThread(bookId, taskId, userId, messages) {
    try {
      const threadRef = doc(db, 'pb-books', bookId, 'tasks', taskId, 'coLearnerThreads', userId);
      await setDoc(
        threadRef,
        {
          messages,
          updatedAt: serverTimestamp(),
          createdAt: serverTimestamp()
        },
        { merge: true }
      );
      return { success: true };
    } catch (error) {
      console.error('Error saving coLearner thread:', error);
      throw error;
    }
  },

  async getPublicTasks(bookId) {
    try {
      const tasksRef = collection(db, 'pb-books', bookId, 'tasks');
      const snapshot = await getDocs(tasksRef);
      
      const now = new Date();
      
      const tasks = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(task => {
          // Only public tasks that don't require sign-in
          if (task.visibility !== 'public') return false;
          if (task.requiresSignIn === true) return false;
          return true;
        })
        .filter(task => {
          // Check time bounds
          if (!task.isTimeBound) return true;
          
          const startDate = task.startDate?.toDate?.() || new Date(task.startDate);
          const endDate = task.endDate?.toDate?.() || new Date(task.endDate);
          
          return now >= startDate && now <= endDate;
        });
      
      return tasks;
    } catch (error) {
      console.error('Error fetching public tasks:', error);
      throw error;
    }
  },

  // Get single task
  async getTask(bookId, taskId) {

    console.log('Fetching task:', bookId, taskId);

    try {
      const taskRef = doc(db, 'pb-books', bookId, 'tasks', taskId);
      const taskDoc = await getDoc(taskRef);
      
      if (!taskDoc.exists()) {
        throw new Error('Task not found');
      }
      
      return { id: taskDoc.id, ...taskDoc.data() };
    } catch (error) {
      console.error('Error fetching task:', error);
      throw error;
    }
  },

  // Check if user can access task
  async canAccessTask(bookId, taskId, userEmail, userId) {
    try {
      const task = await this.getTask(bookId, taskId);
      
      if (task.creatorId === userId) return true;
      if (task.visibility === 'public') return true;
      if (task.visibility === 'private' && task.assignedUsers?.includes(userEmail)) return true;
      
      // Check time bounds
      if (task.isTimeBound) {
        const now = new Date();
        const startDate = task.startDate?.toDate?.() || new Date(task.startDate);
        const endDate = task.endDate?.toDate?.() || new Date(task.endDate);
        
        if (now < startDate || now > endDate) return false;
      }
      
      return false;
    } catch (error) {
      console.error('Error checking task access:', error);
      return false;
    }
  },

  // Assign users to task
  async assignUsers(bookId, taskId, emails) {
    try {
      const taskRef = doc(db, 'pb-books', bookId, 'tasks', taskId);
      await updateDoc(taskRef, {
        assignedUsers: arrayUnion(...emails),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error assigning users:', error);
      throw error;
    }
  },

  // Remove user from task
  async removeUser(bookId, taskId, email) {
    try {
      const taskRef = doc(db, 'pb-books', bookId, 'tasks', taskId);
      await updateDoc(taskRef, {
        assignedUsers: arrayRemove(email),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error removing user:', error);
      throw error;
    }
  },

  // ============= TASK PROGRESS (Leitner Integration) =============

  // Initialize task progress for a user
  async initializeTaskProgress(bookId, taskId, userId, questionIds) {
    try {
      const progressRef = doc(db, 'pb-books', bookId, 'tasks', taskId, 'learnerProgress', userId);
      const progressDoc = await getDoc(progressRef);
      
      if (!progressDoc.exists()) {
        const initialQuestions = {};
        questionIds.forEach(qId => {
          initialQuestions[qId] = {
            box: 1,
            attempts: 0,
            correctAttempts: 0,
            lastReviewed: null
          };
        });
        
        await setDoc(progressRef, {
          questions: initialQuestions,
          startedAt: serverTimestamp(),
          lastActivityAt: serverTimestamp(),
          completedAt: null,
          totalAttempts: 0
        });
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error initializing task progress:', error);
      throw error;
    }
  },

  // Get task progress for a user
  async getTaskProgress(bookId, taskId, userId) {
    try {
      if (!bookId || !taskId || !userId) return null;
      
      const progressRef = doc(db, 'pb-books', bookId, 'tasks', taskId, 'learnerProgress', userId);
      const progressDoc = await getDoc(progressRef);
      
      if (!progressDoc.exists()) {
        return null;
      }
      
      return { id: progressDoc.id, ...progressDoc.data() };
    } catch (error) {
      console.error('Error fetching task progress:', error);
      return null;
    }
  },

  // Update question progress within a task
  async updateTaskQuestionProgress(bookId, taskId, userId, questionId, isCorrect) {
    try {
      const progressRef = doc(db, 'pb-books', bookId, 'tasks', taskId, 'learnerProgress', userId);
      const progressDoc = await getDoc(progressRef);
      
      if (!progressDoc.exists()) {
        throw new Error('Progress not initialized');
      }
      
      const data = progressDoc.data();
      const currentQuestion = data.questions[questionId] || { box: 1, attempts: 0, correctAttempts: 0 };
      
      const newBox = isCorrect 
        ? Math.min(currentQuestion.box + 1, 5)
        : 1;
      
      const updates = {
        [`questions.${questionId}`]: {
          box: newBox,
          attempts: currentQuestion.attempts + 1,
          correctAttempts: currentQuestion.correctAttempts + (isCorrect ? 1 : 0),
          lastReviewed: serverTimestamp()
        },
        lastActivityAt: serverTimestamp(),
        totalAttempts: (data.totalAttempts || 0) + 1
      };
      
      await updateDoc(progressRef, updates);
      
      return { box: newBox };
    } catch (error) {
      console.error('Error updating task question progress:', error);
      throw error;
    }
  },

  // Batch update progress (for test completion)
  async batchUpdateTaskProgress(bookId, taskId, userId, updates) {
    try {
      const progressRef = doc(db, 'pb-books', bookId, 'tasks', taskId, 'learnerProgress', userId);
      const progressDoc = await getDoc(progressRef);
      
      if (!progressDoc.exists()) {
        throw new Error('Progress not initialized');
      }
      
      const data = progressDoc.data();
      const batchUpdates = { lastActivityAt: serverTimestamp() };
      let attemptCount = 0;
      
      updates.forEach(({ questionId, isCorrect }) => {
        const current = data.questions[questionId] || { box: 1, attempts: 0, correctAttempts: 0 };
        const newBox = isCorrect ? Math.min(current.box + 1, 5) : 1;
        
        batchUpdates[`questions.${questionId}`] = {
          box: newBox,
          attempts: current.attempts + 1,
          correctAttempts: current.correctAttempts + (isCorrect ? 1 : 0),
          lastReviewed: serverTimestamp()
        };
        attemptCount++;
      });
      
      batchUpdates.totalAttempts = (data.totalAttempts || 0) + attemptCount;
      
      await updateDoc(progressRef, batchUpdates);
      return { success: true };
    } catch (error) {
      console.error('Error batch updating task progress:', error);
      throw error;
    }
  },

  // Update note reading in learner progress
  async updateNoteReading(bookId, taskId, userId, noteId, { rating, timeSpent, triggeredBy }) {
    try {
      const progressRef = doc(db, 'pb-books', bookId, 'tasks', taskId, 'learnerProgress', userId);
      await updateDoc(progressRef, {
        [`noteReadings.${noteId}`]: {
          readAt: serverTimestamp(),
          rating,
          timeSpent,
          triggeredBy: triggeredBy || null
        },
        lastActivityAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating note reading:', error);
      throw error;
    }
  },

  // Get all learners progress for a task (creator view)
  async getTaskLearnersProgress(bookId, taskId) {
    try {
      const progressRef = collection(db, 'pb-books', bookId, 'tasks', taskId, 'learnerProgress');
      const snapshot = await getDocs(progressRef);
      
      return snapshot.docs.map(doc => ({
        userId: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching task learners progress:', error);
      throw error;
    }
  },

  async getAllTaskLearnersProgress(bookId, taskId) {
    try {
      const progressRef = collection(db, 'pb-books', bookId, 'tasks', taskId, 'learnerProgress');
      const snapshot = await getDocs(progressRef);
      
      return snapshot.docs.map(doc => ({
        userId: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching all task learners progress:', error);
      return [];
    }
  },

  // Save task attempt result
  async saveTaskAttempt(bookId, taskId, userId, result) {
    try {
      const attemptsRef = collection(db, 'pb-books', bookId, 'tasks', taskId, 'attempts');
      
      const attempt = {
        userId,
        score: result.score,
        totalQuestions: result.totalQuestions,
        percentage: result.percentage,
        answers: result.answers || {},
        duration: result.duration || 0,
        completedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(attemptsRef, attempt);
      return { id: docRef.id, ...attempt };
    } catch (error) {
      console.error('Error saving task attempt:', error);
      throw error;
    }
  },

  // Get user's attempts for a task
  async getTaskAttempts(bookId, taskId, userId) {
    try {
      const attemptsRef = collection(db, 'pb-books', bookId, 'tasks', taskId, 'attempts');
      const q = query(attemptsRef, where('userId', '==', userId), orderBy('completedAt', 'desc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching task attempts:', error);
      throw error;
    }
  },

  // Search users by email (for assignment)
  async searchUsersByEmail(emailQuery) {
    try {
      const usersRef = collection(db, 'pb-users');
      const snapshot = await getDocs(usersRef);
      
      const results = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(user => 
          user.email?.toLowerCase().includes(emailQuery.toLowerCase())
        )
        .slice(0, 10);
      
      return results;
    } catch (error) {
      console.error('Error searching users:', error);
      return [];
    }
  },

  // Upload task OG image
  async uploadTaskImage(bookId, taskId, imageFile) {
    try {
      const imagePath = `pb-books/${bookId}/tasks/og-image-${Date.now()}.${imageFile.name.split('.').pop()}`;
      const imageRef = ref(storage, imagePath);
      await uploadBytes(imageRef, imageFile);
      const url = await getDownloadURL(imageRef);
      
      await this.updateTask(bookId, taskId, { ogImage: url });
      
      return url;
    } catch (error) {
      console.error('Error uploading task image:', error);
      throw error;
    }
  },

  async uploadTaskPdf(bookId, taskId, pdfBlob, filename, options = {}) {
    try {
      const persistToTask = options.persistToTask !== false;
      const baseName = (filename || `task-${Date.now()}.pdf`).trim();
      const ext = baseName.toLowerCase().endsWith('.pdf') ? '' : '.pdf';
      const safeFilename = `${baseName.replace(/[^\w.\- ]+/g, '_')}${ext}`;
      const taskFolder = taskId || 'drafts';
      const pdfPath = `pb-books/${bookId}/tasks/${taskFolder}/pdf/${Date.now()}-${safeFilename}`;
      const pdfRef = ref(storage, pdfPath);

      await uploadBytes(pdfRef, pdfBlob, {
        contentType: 'application/pdf',
        cacheControl: 'public,max-age=3600'
      });

      const url = await getDownloadURL(pdfRef);

      if (persistToTask && taskId) {
        await this.updateTask(bookId, taskId, {
          taskPdfUrl: url,
          taskPdfFilename: safeFilename,
          taskPdfUploadedAt: serverTimestamp()
        });
      }

      return { url, filename: safeFilename };
    } catch (error) {
      console.error('Error uploading task PDF:', error);
      throw error;
    }
  },

  async uploadGuidedSourcePdf(bookId, file) {
    try {
      if (!file) {
        throw new Error('Missing PDF file');
      }
      const safeName = `${Date.now()}-${(file.name || 'guided-source.pdf').replace(/[^\w.\- ]+/g, '_')}`;
      const pdfPath = `pb-books/${bookId}/tasks/guided-source/${safeName}`;
      const pdfRef = ref(storage, pdfPath);

      await uploadBytes(pdfRef, file, {
        contentType: file.type || 'application/pdf',
        cacheControl: 'public,max-age=3600'
      });

      const url = await getDownloadURL(pdfRef);
      return { url, filename: safeName, path: pdfPath };
    } catch (error) {
      console.error('Error uploading guided source PDF:', error);
      throw error;
    }
  },

  async generateGuidedReconstruction(payload) {
    try {
      const callable = httpsCallable(functions, 'generateGuidedReconstructionTask');
      const result = await callable(payload);
      return result?.data || {};
    } catch (error) {
      console.error('Error generating guided reconstruction task:', error);
      throw error;
    }
  },

  // ============================================
  // TASK RESULTS (Attempt-based tracking)
  // ============================================

  // Save a task attempt result
  async saveTaskResult(bookId, taskId, userId, result) {
    try {
      const resultsRef = collection(db, 'pb-books', bookId, 'taskResults');
      
      const resultData = {
        taskId,
        taskName: result.taskName,
        userId,
        userEmail: result.userEmail || null,
        score: result.score,
        totalQuestions: result.totalQuestions,
        totalMarks: result.totalMarks || result.score,
        maxMarks: result.maxMarks || result.totalQuestions,
        percentage: result.totalQuestions > 0 
          ? Math.round((result.score / result.totalQuestions) * 100) 
          : 0,
        timeSpent: result.timeSpent || 0,
        questionResults: result.questionResults || {}, // { questionId: { correct, attempts, timeSpent, answer } }
        attemptNumber: result.attemptNumber || 1,
        completedAt: serverTimestamp(),
        createdAt: serverTimestamp()
      };
      
      const docRef = await addDoc(resultsRef, resultData);
      
      // Update task analytics
      await this.updateTaskAnalytics(bookId, taskId, resultData);
      
      return { id: docRef.id, ...resultData };
    } catch (error) {
      console.error('Error saving task result:', error);
      throw error;
    }
  },

  // Get all results for a task
  async getTaskResults(bookId, taskId) {
    try {
      const resultsRef = collection(db, 'pb-books', bookId, 'taskResults');
      const q = query(
        resultsRef, 
        where('taskId', '==', taskId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching task results:', error);
      throw error;
    }
  },

  // Get user's results for a specific task
  async getUserTaskResults(bookId, taskId, userId) {
    try {
      const resultsRef = collection(db, 'pb-books', bookId, 'taskResults');
      const q = query(
        resultsRef,
        where('taskId', '==', taskId),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching user task results:', error);
      throw error;
    }
  },

  // Get all results for a user across all tasks
  async getAllUserTaskResults(bookId, userId) {
    try {
      const resultsRef = collection(db, 'pb-books', bookId, 'taskResults');
      const q = query(
        resultsRef,
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching all user task results:', error);
      throw error;
    }
  },

  async getAllBookTaskResults(bookId) {
    try {
      const resultsRef = collection(db, 'pb-books', bookId, 'taskResults');
      const q = query(resultsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching all book task results:', error);
      return [];
    }
  },

  // ============================================
  // TASK ANALYTICS (Aggregate stats)
  // ============================================

  // Update aggregate analytics for a task
  async updateTaskAnalytics(bookId, taskId, resultData) {
    try {
      const analyticsRef = doc(db, 'pb-books', bookId, 'taskAnalytics', taskId);
      const analyticsDoc = await getDoc(analyticsRef);
      
      const questionStats = {};
      
      // Process each question result
      Object.entries(resultData.questionResults || {}).forEach(([qId, qResult]) => {
        questionStats[qId] = {
          attempts: increment(1),
          correct: qResult.correct ? increment(1) : increment(0),
          totalTimeSpent: increment(qResult.timeSpent || 0)
        };
      });
      
      if (analyticsDoc.exists()) {
        const existing = analyticsDoc.data();
        
        // Merge question stats
        const mergedQuestionStats = { ...existing.questionStats };
        Object.entries(questionStats).forEach(([qId, stats]) => {
          if (mergedQuestionStats[qId]) {
            mergedQuestionStats[qId] = {
              attempts: (mergedQuestionStats[qId].attempts || 0) + 1,
              correct: (mergedQuestionStats[qId].correct || 0) + (resultData.questionResults?.[qId]?.correct ? 1 : 0),
              totalTimeSpent: (mergedQuestionStats[qId].totalTimeSpent || 0) + (resultData.questionResults?.[qId]?.timeSpent || 0)
            };
          } else {
            mergedQuestionStats[qId] = {
              attempts: 1,
              correct: resultData.questionResults?.[qId]?.correct ? 1 : 0,
              totalTimeSpent: resultData.questionResults?.[qId]?.timeSpent || 0
            };
          }
        });
        
        await updateDoc(analyticsRef, {
          totalAttempts: (existing.totalAttempts || 0) + 1,
          totalScore: (existing.totalScore || 0) + resultData.score,
          totalMaxScore: (existing.totalMaxScore || 0) + resultData.maxMarks,
          uniqueUsers: arrayUnion(resultData.userId),
          averagePercentage: Math.round(
            ((existing.totalScore || 0) + resultData.score) / 
            ((existing.totalMaxScore || 0) + resultData.maxMarks) * 100
          ),
          questionStats: mergedQuestionStats,
          lastAttemptAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } else {
        const initialQuestionStats = {};
        Object.entries(resultData.questionResults || {}).forEach(([qId, qResult]) => {
          initialQuestionStats[qId] = {
            attempts: 1,
            correct: qResult.correct ? 1 : 0,
            totalTimeSpent: qResult.timeSpent || 0
          };
        });
        
        await setDoc(analyticsRef, {
          taskId,
          totalAttempts: 1,
          totalScore: resultData.score,
          totalMaxScore: resultData.maxMarks,
          uniqueUsers: [resultData.userId],
          averagePercentage: resultData.percentage,
          questionStats: initialQuestionStats,
          lastAttemptAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error updating task analytics:', error);
      // Don't throw - analytics update failure shouldn't break the flow
    }
  },

  // Get task analytics
  async getTaskAnalytics(bookId, taskId) {
    try {
      const analyticsRef = doc(db, 'pb-books', bookId, 'taskAnalytics', taskId);
      const analyticsDoc = await getDoc(analyticsRef);
      
      if (analyticsDoc.exists()) {
        return { id: analyticsDoc.id, ...analyticsDoc.data() };
      }
      return null;
    } catch (error) {
      console.error('Error fetching task analytics:', error);
      throw error;
    }
  },

  // Get all task analytics for a book
  async getAllTaskAnalytics(bookId) {
    try {
      const analyticsRef = collection(db, 'pb-books', bookId, 'taskAnalytics');
      const snapshot = await getDocs(analyticsRef);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching all task analytics:', error);
      throw error;
    }
  },

  async getTaskAnalyticsWithLearners(bookId, taskId) {
    try {
      // Get all learner progress (this is where the real data lives)
      const learnersProgress = await this.getAllTaskLearnersProgress(bookId, taskId);

      // Get task results if any exist
      const taskResults = await this.getTaskResults(bookId, taskId);

      // Helper to flatten nested question structures
      const flattenQuestions = (questions) => {
        const flattened = {};
        Object.entries(questions || {}).forEach(([key, value]) => {
          if (value && typeof value.box === 'number') {
            flattened[key] = value;
          } else if (value && typeof value === 'object' && !Array.isArray(value)) {
            Object.entries(value).forEach(([subKey, subValue]) => {
              if (subValue && typeof subValue.box === 'number') {
                flattened[subKey] = subValue;
              }
            });
          }
        });
        return flattened;
      };

      // Calculate per-learner summaries from progress
      const learnerSummaries = {};
      
      learnersProgress.forEach(progress => {
        const flatQuestions = flattenQuestions(progress.questions);
        const questionsArray = Object.values(flatQuestions);
        
        const totalAttempts = questionsArray.reduce((sum, q) => sum + (q.attempts || 0), 0);
        const totalCorrect = questionsArray.reduce((sum, q) => sum + (q.correctAttempts || 0), 0);
        const mastered = questionsArray.filter(q => q.box >= 4).length;
        
        learnerSummaries[progress.userId] = {
          userId: progress.userId,
          progress: progress,
          results: [],
          stats: {
            attempts: totalAttempts,
            questionsCount: questionsArray.length,
            masteredCount: mastered,
            masteryPercent: questionsArray.length > 0 
              ? Math.round((mastered / questionsArray.length) * 100) 
              : 0,
            avgScore: totalAttempts > 0 
              ? Math.round((totalCorrect / totalAttempts) * 100) 
              : 0,
            bestScore: totalAttempts > 0 
              ? Math.round((totalCorrect / totalAttempts) * 100) 
              : 0,
            totalTime: 0,
            lastAttempt: progress.lastActivityAt
          }
        };
      });

      // Add results data if exists
      taskResults.forEach(result => {
        if (learnerSummaries[result.userId]) {
          learnerSummaries[result.userId].results.push(result);
          // Update stats from results if available
          const learner = learnerSummaries[result.userId];
          if (result.percentage > learner.stats.bestScore) {
            learner.stats.bestScore = result.percentage;
          }
          learner.stats.totalTime += result.timeSpent || 0;
        }
      });

      // Calculate aggregate analytics
      const allLearners = Object.values(learnerSummaries);
      const analytics = {
        totalLearners: allLearners.length,
        totalAttempts: allLearners.reduce((sum, l) => sum + l.stats.attempts, 0),
        averageScore: allLearners.length > 0
          ? Math.round(allLearners.reduce((sum, l) => sum + l.stats.avgScore, 0) / allLearners.length)
          : 0,
        averageMastery: allLearners.length > 0
          ? Math.round(allLearners.reduce((sum, l) => sum + l.stats.masteryPercent, 0) / allLearners.length)
          : 0
      };

      return {
        analytics,
        learnersProgress,
        taskResults,
        learnerSummaries: allLearners
      };
    } catch (error) {
      console.error('Error fetching task analytics with learners:', error);
      throw error;
    }
  },

  // Get leaderboard for a task
  async getTaskLeaderboard(bookId, taskId, limitCount = 10) {
    try {
      const resultsRef = collection(db, 'pb-books', bookId, 'taskResults');
      const q = query(
        resultsRef,
        where('taskId', '==', taskId),
        orderBy('percentage', 'desc')
      );
      const snapshot = await getDocs(q);
      
      // Dedupe by userId, keeping best score
      const userBestScores = new Map();
      snapshot.docs.forEach(doc => {
        const data = { id: doc.id, ...doc.data() };
        if (!userBestScores.has(data.userId) || 
            userBestScores.get(data.userId).percentage < data.percentage) {
          userBestScores.set(data.userId, data);
        }
      });
      
      return Array.from(userBestScores.values())
        .sort((a, b) => b.percentage - a.percentage)
        .slice(0, limitCount);
    } catch (error) {
      console.error('Error fetching task leaderboard:', error);
      return [];
    }
  },

  async getTaskQuestionAnalytics(bookId, taskId) {
    try {
      const learnersProgress = await this.getAllTaskLearnersProgress(bookId, taskId);
      
      // Helper to flatten nested question structures
      const flattenQuestions = (questions) => {
        const flattened = {};
        Object.entries(questions || {}).forEach(([key, value]) => {
          if (value && typeof value.box === 'number') {
            flattened[key] = value;
          } else if (value && typeof value === 'object' && !Array.isArray(value)) {
            Object.entries(value).forEach(([subKey, subValue]) => {
              if (subValue && typeof subValue.box === 'number') {
                flattened[subKey] = subValue;
              }
            });
          }
        });
        return flattened;
      };
      
      const questionStats = {};
      
      // Aggregate from all learners' progress
      learnersProgress.forEach(progress => {
        const flatQuestions = flattenQuestions(progress.questions);
        
        Object.entries(flatQuestions).forEach(([qId, qData]) => {
          if (!questionStats[qId]) {
            questionStats[qId] = {
              questionId: qId,
              totalAttempts: 0,
              correctAttempts: 0,
              totalTime: 0,
              learnerBoxes: [],
              learnerCount: 0
            };
          }
          
          questionStats[qId].totalAttempts += qData.attempts || 0;
          questionStats[qId].correctAttempts += qData.correctAttempts || 0;
          questionStats[qId].learnerBoxes.push(qData.box || 1);
          questionStats[qId].learnerCount++;
        });
      });
      
      // Calculate derived stats
      Object.values(questionStats).forEach(stat => {
        stat.accuracy = stat.totalAttempts > 0 
          ? Math.round((stat.correctAttempts / stat.totalAttempts) * 100) 
          : 0;
        stat.avgBox = stat.learnerBoxes.length > 0
          ? (stat.learnerBoxes.reduce((a, b) => a + b, 0) / stat.learnerBoxes.length).toFixed(1)
          : '1';
        stat.difficulty = stat.accuracy < 40 ? 'hard' : stat.accuracy < 70 ? 'medium' : 'easy';
      });
      
      return questionStats;
    } catch (error) {
      console.error('Error fetching task question analytics:', error);
      return {};
    }
  },

  async ensureTaskAnalytics(bookId, taskId) {
    try {
      const analyticsRef = doc(db, 'pb-books', bookId, 'taskAnalytics', taskId);
      const analyticsDoc = await getDoc(analyticsRef);
      
      if (!analyticsDoc.exists()) {
        await setDoc(analyticsRef, {
          taskId,
          totalAttempts: 0,
          totalScore: 0,
          totalMaxScore: 0,
          uniqueUsers: [],
          averagePercentage: 0,
          questionStats: {},
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      
      return true;
    } catch (error) {
      console.error('Error ensuring task analytics:', error);
      return false;
    }
  },

  // Generate shareable task URL
  getTaskShareUrl(bookId, taskId, customDomain = null) {
    const baseUrl = customDomain || window.location.origin;
    return `${baseUrl}/tasks/${taskId}`;
  }
};
